export type AuditItemType = "check" | "reading" | "note";
export type AuditEvidence = "none" | "photo" | "video" | "optional" | "note";

export interface AuditFormItem {
  id: string;
  label: string;
  type: AuditItemType;
  evidence: AuditEvidence;
  hint: string;
}

export interface AuditFormSection {
  id: string;
  title: string;
  items: AuditFormItem[];
}

export interface AuditForm {
  id: string;
  name: string;
  period: "daily" | "weekly" | "monthly";
  description: string;
  sections: AuditFormSection[];
}

const ai = (id: string, label: string, type: AuditItemType = "check", evidence: AuditEvidence = "optional", hint = ""): AuditFormItem => ({ id, label, type, evidence, hint });

const daily: AuditForm = {
  id: "af-daily",
  name: "Daily Audit",
  period: "daily",
  description: "Management audit covering care, environment, medicines, kitchen, activities, safety, records and staffing.",
  sections: [
    { id: "da-env", title: "Environment and Cleanliness", items: [
      ai("da-env-1", "All rooms clean, tidy and free from odour", "check", "photo"),
      ai("da-env-2", "Toilets and bathrooms cleaned and signed on the schedule", "check", "photo"),
      ai("da-env-3", "Hand hygiene stations stocked at every point", "check", "photo"),
      ai("da-env-4", "Temperature comfortable and ventilation working", "reading", "optional", "Record degrees C"),
      ai("da-env-5", "Furniture and fittings clean and in good repair", "check"),
    ]},
    { id: "da-care", title: "Care Delivery", items: [
      ai("da-care-1", "Care plans up to date and signed", "check", "note"),
      ai("da-care-2", "Residents seen and spoken with during the day", "check", "video"),
      ai("da-care-3", "Call bells answered promptly when tested", "check"),
      ai("da-care-4", "Pressure care repositioning charts completed", "check", "photo"),
      ai("da-care-5", "Fluid rounds completed and recorded", "check", "note"),
    ]},
    { id: "da-meds", title: "Medicines", items: [
      ai("da-meds-1", "MAR charts signed with no unexplained gaps", "check", "photo"),
      ai("da-meds-2", "Controlled drugs book balanced and signed", "check", "note"),
      ai("da-meds-3", "Medicines cabinet locked and keys controlled", "check"),
      ai("da-meds-4", "Medicines fridge temperature 2 to 8 C recorded", "reading", "photo"),
    ]},
    { id: "da-kit", title: "Kitchen and Dining", items: [
      ai("da-kit-1", "Fridge 0 to 5 C and freezer temperatures recorded", "reading", "photo"),
      ai("da-kit-2", "Food in date, covered and labelled", "check", "photo"),
      ai("da-kit-3", "Special diets followed at meal times", "check", "note"),
      ai("da-kit-4", "Dining room set and welcoming for meals", "check", "photo"),
    ]},
    { id: "da-act", title: "Activities and Wellbeing", items: [
      ai("da-act-1", "Activities programme displayed and running today", "check", "photo"),
      ai("da-act-2", "Residents engaged and enjoying sessions", "check", "video"),
      ai("da-act-3", "Residents who declined activities offered alternatives", "check", "note"),
    ]},
    { id: "da-safe", title: "Health and Safety", items: [
      ai("da-safe-1", "Fire exits clear and fire doors closed", "check", "photo"),
      ai("da-safe-2", "No trip hazards in corridors and rooms", "check", "photo"),
      ai("da-safe-3", "Incidents from the last shift reviewed and actioned", "check", "note"),
    ]},
    { id: "da-rec", title: "Records", items: [
      ai("da-rec-1", "Care notes written up to date for the shift", "check"),
      ai("da-rec-2", "Handover sheet completed accurately", "check"),
    ]},
    { id: "da-staff", title: "Staffing and Management", items: [
      ai("da-staff-1", "Staff on rota match dependency for today", "check", "note"),
      ai("da-staff-2", "Staff aware of the priorities for the day", "check"),
    ]},
  ],
};
const weekly: AuditForm = {
  id: "af-weekly",
  name: "Weekly Audit",
  period: "weekly",
  description: "Weekly management audit of safety tests, water, equipment, infection control, medicines, care planning and governance.",
  sections: [
    { id: "wf-fire", title: "Fire and Safety Tests", items: [
      ai("wf-fire-1", "Weekly fire alarm test completed and logged", "check", "note"),
      ai("wf-fire-2", "Fire doors and escape routes checked", "check", "photo"),
      ai("wf-fire-3", "Extinguishers in position with gauges in green", "check", "photo"),
      ai("wf-fire-4", "Emergency lighting check completed and logged", "check", "note"),
    ]},
    { id: "wf-water", title: "Water Safety", items: [
      ai("wf-water-1", "Sentinel tap temperatures recorded", "reading", "photo"),
      ai("wf-water-2", "Shower temperatures recorded", "reading"),
      ai("wf-water-3", "Infrequently used outlets flushed", "check", "note"),
      ai("wf-water-4", "Water safety log reviewed and signed", "check", "note"),
    ]},
    { id: "wf-equip", title: "Equipment and Moving Handling", items: [
      ai("wf-equip-1", "Hoist pre-use checks and LOLER dates verified", "check", "photo"),
      ai("wf-equip-2", "Slings checked for wear and labelling", "check", "photo"),
      ai("wf-equip-3", "Wheelchair brakes, tyres and footplates checked", "check", "photo"),
      ai("wf-equip-4", "Profiling beds and mattresses checked", "check", "photo"),
      ai("wf-equip-5", "Call bells tested in every room", "check", "note"),
    ]},
    { id: "wf-ipc", title: "Infection Prevention and Control", items: [
      ai("wf-ipc-1", "Cleaning schedules completed and audited", "check", "photo"),
      ai("wf-ipc-2", "PPE stock levels checked and topped up", "check"),
      ai("wf-ipc-3", "Clinical waste area clean, locked and within capacity", "check", "photo"),
      ai("wf-ipc-4", "Hand hygiene audit completed", "check", "note"),
    ]},
    { id: "wf-meds", title: "Medicines Review", items: [
      ai("wf-meds-1", "MAR audit sample completed", "check", "note"),
      ai("wf-meds-2", "Controlled drugs book checked against stock", "check", "note"),
      ai("wf-meds-3", "Medicines fridge log reviewed for excursions", "reading", "photo"),
      ai("wf-meds-4", "Outstanding pharmacy orders chased", "check", "note"),
    ]},
    { id: "wf-care", title: "Care Planning Review", items: [
      ai("wf-care-1", "Care plans reviewed for changes this week", "check", "note"),
      ai("wf-care-2", "Weights completed for at risk residents", "check", "note"),
      ai("wf-care-3", "Falls review completed for any falls this week", "check", "note"),
      ai("wf-care-4", "Fluid and nutrition charts reviewed", "check"),
    ]},
    { id: "wf-gov", title: "Governance Checks", items: [
      ai("wf-gov-1", "Incidents this week reviewed and graded", "check", "note"),
      ai("wf-gov-2", "Complaints log reviewed", "check", "note"),
      ai("wf-gov-3", "Supervision due list chased", "check", "note"),
      ai("wf-gov-4", "Training matrix reviewed and gaps chased", "check", "note"),
    ]},
  ],
};

const monthly: AuditForm = {
  id: "af-monthly",
  name: "Monthly Audit",
  period: "monthly",
  description: "Monthly deep dive covering quality, outcomes, people, medicines governance, governance trends, environment and CQC readiness.",
  sections: [
    { id: "mf-quality", title: "Quality and Audit Programme", items: [
      ai("mf-quality-1", "Audit calendar progress reviewed", "check", "note"),
      ai("mf-quality-2", "Action plan closure rate reviewed", "check", "note"),
      ai("mf-quality-3", "Audit scores trended and discussed", "check", "note"),
      ai("mf-quality-4", "Learning shared with the whole team", "check", "note"),
    ]},
    { id: "mf-outcomes", title: "Resident Outcomes", items: [
      ai("mf-outcomes-1", "Falls rate and pressure damage prevalence reviewed", "check", "note"),
      ai("mf-outcomes-2", "Weight and nutrition trends reviewed", "check", "note"),
      ai("mf-outcomes-3", "Infection and outbreak events reviewed", "check", "note"),
      ai("mf-outcomes-4", "Resident and family feedback reviewed", "check", "note"),
    ]},
    { id: "mf-people", title: "Staffing, Training and Supervision", items: [
      ai("mf-people-1", "Mandatory training compliance reviewed", "check", "note"),
      ai("mf-people-2", "Supervision and appraisal completion reviewed", "check", "note"),
      ai("mf-people-3", "New starter induction progress reviewed", "check", "note"),
      ai("mf-people-4", "Agency usage and spend reviewed", "check", "note"),
    ]},
    { id: "mf-meds", title: "Medicines Governance", items: [
      ai("mf-meds-1", "Medication errors and near misses trended", "check", "note"),
      ai("mf-meds-2", "Pharmacy support and contract reviewed", "check", "note"),
      ai("mf-meds-3", "Medicines storage audit completed", "check", "photo"),
    ]},
    { id: "mf-gov", title: "Incidents, Complaints and Safeguarding", items: [
      ai("mf-gov-1", "Incident, complaint and safeguarding trends reviewed", "check", "note"),
      ai("mf-gov-2", "CQC notifications reviewed for timeliness", "check", "note"),
      ai("mf-gov-3", "Policies due for review listed and assigned", "check", "note"),
      ai("mf-gov-4", "Registration matters checked", "check", "note"),
    ]},
    { id: "mf-env", title: "Environment, Maintenance and Safety", items: [
      ai("mf-env-1", "Maintenance backlog reviewed and prioritised", "check", "note"),
      ai("mf-env-2", "Fire safety equipment service dates checked", "check", "photo"),
      ai("mf-env-3", "Water safety file reviewed", "check", "note"),
      ai("mf-env-4", "Garden and outdoor areas safety checked", "check", "photo"),
    ]},
    { id: "mf-cqc", title: "CQC Readiness", items: [
      ai("mf-cqc-1", "Evidence folders updated for each key question", "check", "note"),
      ai("mf-cqc-2", "Mock inspection questions practised with staff", "check", "note"),
      ai("mf-cqc-3", "Staff awareness of key questions spot checked", "check", "note"),
      ai("mf-cqc-4", "Inspection action plan updated", "check", "note"),
    ]},
  ],
};

export const auditForms: AuditForm[] = [daily, weekly, monthly];